import React, { useState, useEffect, useContext } from "react";
import {
  Button,
  Box,
  Typography,
  TextField,
  Autocomplete,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Paper,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import UserContext from "../../../UserContext";
import SlidingSidebar, {
  ButtonControls,
} from "../../../Components/SlidingSidebar";
import axios from "axios";

interface State {
  id: number;
  state: string;
  state_code: string;
}

const AddTeam: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [states, setStates] = useState<State[]>([]);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    role: "Brand",
    selectedStates: [] as State[],
    user_access: {
      tags: [],
      user_status: {
        Admin: "No",
        Active: "Yes",
        Verified: "Yes",
      },
    },
  });
  const [error, setError] = useState<string | null>(null);

  const {} = useContext(UserContext) || {
    selectedBrandId: null,
    selectedSupplierId: null,
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${import.meta.env.VITE_API_URL}/settings/util-data?tables=usa_states`
        );
        const data = await response.data;
        setStates(data.usa_states || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      role: "Brand",
      selectedStates: [],
      user_access: {
        tags: [],
        user_status: {
          Admin: "No",
          Active: "Yes",
          Verified: "Yes",
        },
      },
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/brands/staff`,
        formData
      );
      if (response.status === 200) {
        console.log("User created successfully:", response.data);
        handleClose();
      } else {
        throw new Error(
          response.data.details ||
            response.data.error ||
            "Failed to create user"
        );
      }
    } catch (error) {
      console.error("Error creating user:", error);
      setError(
        error instanceof Error ? error.message : "An unexpected error occurred"
      );
    }
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={handleOpen}
        startIcon={<CloudUploadIcon />}
      >
        Add User
      </Button>

      <SlidingSidebar open={open} onClose={handleClose} width="500px">
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ height: "100%", display: "flex", flexDirection: "column" }}
        >
          <Box sx={{ flexGrow: 1, overflow: "auto", pb: 2 }}>
            <Stack spacing={0.5} sx={{ p: 2 }}>
              <Paper elevation={0} sx={{ p: 1 }}>
                <Stack spacing={2}>
                  <Typography variant="h6">Basic Information</Typography>
                  <TextField
                    label="First Name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    size="small"
                    fullWidth
                    required
                  />
                  <TextField
                    label="Last Name"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    size="small"
                    fullWidth
                    required
                  />
                  <TextField
                    label="Email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    size="small"
                    fullWidth
                    required
                  />
                </Stack>
              </Paper>

              <Paper elevation={0} sx={{ p: 1 }}>
                <Stack spacing={2}>
                  <Typography variant="h6">Role & Markets</Typography>
                  <FormControl fullWidth size="small">
                    <InputLabel id="role-label">Role</InputLabel>
                    <Select
                      labelId="role-label"
                      name="role"
                      value={formData.role}
                      label="Role"
                      onChange={(e) =>
                        handleInputChange({
                          target: { name: "role", value: e.target.value },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                      required
                    >
                      <MenuItem value="Brand">Brand</MenuItem>
                      <MenuItem value="Supplier">Supplier</MenuItem>
                    </Select>
                  </FormControl>

                  <Autocomplete
                    multiple
                    options={states}
                    getOptionLabel={(option) => option.state}
                    value={formData.selectedStates}
                    onChange={(_, newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        selectedStates: newValue,
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Markets" size="small" />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option.state_code}
                          {...getTagProps({ index })}
                          size="small"
                        />
                      ))
                    }
                  />
                </Stack>
              </Paper>

              {error && (
                <Typography color="error" sx={{ mt: 2, px: 1 }}>
                  {error}
                </Typography>
              )}
            </Stack>
          </Box>

          <ButtonControls>
            <Button type="submit" variant="contained" color="primary">
              Create User
            </Button>
            <Button onClick={handleClose} variant="outlined">
              Cancel
            </Button>
          </ButtonControls>
        </Box>
      </SlidingSidebar>
    </>
  );
};

export default AddTeam;
